"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  DEFAULT_TABLE_STORAGE_KEYS,
  createTableLabelFn,
  type TableLabels,
  type TableStorageKeys,
} from "../app";
import { DEFAULT_TABLE_COMPONENTS } from "./default-components";
import { TableLabelsContext } from "./labels-context";
import type { TableComponents } from "./table-components";
import { defaultTableUrlAdapter, type TableUrlAdapter } from "./url-adapter";

export interface TableConfig {
  /** Язык подписей списков и форматирования чисел (по умолчанию `en-US`). */
  locale: string;
  storageKeys: TableStorageKeys;
  components: TableComponents;
  urlAdapter: TableUrlAdapter;
}

const DEFAULT_CONFIG: TableConfig = {
  locale: DEFAULT_LOCALE,
  storageKeys: DEFAULT_TABLE_STORAGE_KEYS,
  components: DEFAULT_TABLE_COMPONENTS,
  urlAdapter: defaultTableUrlAdapter,
};

const TableConfigContext = createContext<TableConfig>(DEFAULT_CONFIG);

export interface TableProviderProps {
  /** Переопределения текстов поверх `DEFAULT_TABLE_LABELS_EN`. */
  labels?: Partial<TableLabels>;
  locale?: string;
  storageKeys?: Partial<TableStorageKeys>;
  /** Слоты; не заданные берутся из значений по умолчанию. */
  components?: Partial<TableComponents>;
  urlAdapter?: TableUrlAdapter;
  children: ReactNode;
}

/** Необязательный провайдер: без него таблица работает на значениях по умолчанию. */
export function TableProvider({
  labels,
  locale,
  storageKeys,
  components,
  urlAdapter,
  children,
}: TableProviderProps) {
  const labelFn = useMemo(() => createTableLabelFn(labels), [labels]);
  const config = useMemo<TableConfig>(
    () => ({
      locale: locale ?? DEFAULT_CONFIG.locale,
      storageKeys: { ...DEFAULT_CONFIG.storageKeys, ...storageKeys },
      components: { ...DEFAULT_CONFIG.components, ...components },
      urlAdapter: urlAdapter ?? DEFAULT_CONFIG.urlAdapter,
    }),
    [locale, storageKeys, components, urlAdapter],
  );
  return (
    <TableLabelsContext.Provider value={labelFn}>
      <TableConfigContext.Provider value={config}>{children}</TableConfigContext.Provider>
    </TableLabelsContext.Provider>
  );
}

export function useTableConfig(): TableConfig {
  return useContext(TableConfigContext);
}

export function useTableComponents(): TableComponents {
  return useContext(TableConfigContext).components;
}
